import { FourOhFour } from '@cimpress/react-components';
import { type JSX, Suspense } from 'react';
import { AdaptToProduct } from '../Pages/AdaptToProduct';
import AdaptToLayoutPage from '../Pages/AdaptToLayoutPage/AdaptToLayoutPage';
import { AdjustForSubstratePage } from '../Pages/AdjustForSubstratePage';
import { AdjustForSurfacePage } from '../Pages/AdjustForSurfacePage';
import { CimDocEnrichmentPage } from '../Pages/CimDocEnrichmentPage';
import { CompositePage } from '../Pages/CompositionComposePage';
import { ComposeStylizedAssetsPage } from '../Pages/CompositionGenerateStylizedAssetsPage';
import { ConvertRasterToVector } from '../Pages/ConvertRasterToVector';
import { DesignAgentMovedPage } from '../Pages/DesignAgentMovedPage';
import { PpagGalleryPage } from '../Pages/DiamondParity/PpagGalleryPage';
import EditImagePage from '../Pages/EditImage/v3/editImagePage';
import { FontSwapPage } from '../Pages/FontSwapPage';
import GenerateImage from '../Pages/GenerateImage';
import { GenerateLogoPage } from '../Pages/GenerateLogo/v3/GenerateLogoPage';
import { GenerateLogoV4Page } from '../Pages/GenerateLogo/v4/GenerateLogoV4Page';
import { GenerateLogoPromptPage } from '../Pages/GenerateLogoPromptPage';
import { GeneratePage } from '../Pages/GeneratePage';
import { GeneratePatternPage } from '../Pages/GeneratePattern/generatePatternPage';
import { ImproveArtworkContrastPage } from '../Pages/ImproveArtworkContrastPage';
import { InspirationGeneratePage } from '../Pages/InspirationGeneratePage';
import LegacyToDesignJobComparison from '../Pages/DesignJobsApiComparison/LegacyToDesignJobComparison';
import { ResizePage } from '../Pages/ResizePage';
import { StylizePhotoPage } from '../Pages/StylizePhotoPage';
import { TransferCustomization } from '../Pages/TransferCustomizationPage';
import { TransferStyle } from '../Pages/TransferStylePage';
import config from '../config';
import {
    AdaptToProductPage,
    CompositionAutoLayoutPage,
    DiamondParityAutoLayoutPage,
    DesignJobPage,
    DiamondParityDynamicPage,
    DiamondParityPage,
    DiamondParityStaticPage,
    DynamicLineNormalisationReport,
    DiamondUrlConverterPage,
    EditImagePageV0,
    HiddenPageIndexPage,
    PersonalizedGallery,
    PreviewCardVisualizationsPage,
    SettingsPage,
} from './lazyLoaders';
import { PageLoadFallback } from './PageLoadFallback';

export interface Route {
    href: string;
    externalHref?: string;
    title: string;
    element: JSX.Element;
}

export interface RouteWithProblemSpace extends Route {
    problemSpace: string;
}

export interface ProblemSpaceRoutes {
    name: string;
    routes: Route[];
}

const artworkAdaptationRoutes: Route[] = [
    {
        href: 'resize',
        title: 'Resize',
        element: <ResizePage />,
    },
    {
        href: 'adjustforsubstrate',
        title: 'Adapt to Substrate Color',
        element: <AdjustForSubstratePage />,
    },
    {
        href: 'adjustforsurface',
        title: 'Adapt to Product Surface',
        element: <AdjustForSurfacePage />,
    },
    {
        href: 'adaptToProduct',
        title: 'Adapt to Product',
        element: <AdaptToProduct />,
    },
    {
        href: 'adaptToLayout',
        title: 'Adapt to Layout',
        element: <AdaptToLayoutPage />,
    },
];

const artworkCompositionRoutes: Route[] = [
    {
        href: 'generate',
        title: 'Generate',
        element: <GeneratePage />,
    },
    {
        href: 'composite',
        title: 'Compose',
        element: <CompositePage />,
    },
    {
        href: 'fontswap',
        title: 'Font Swap',
        element: <FontSwapPage />,
    },
];

const artworkPreservationRoutes: Route[] = [
    {
        href: 'transfer',
        title: 'Transfer Customizations',
        element: <TransferCustomization />,
    },
    {
        href: 'transferstyle',
        title: 'Transfer Style',
        element: <TransferStyle />,
    },
];

const artworkQualityRoutes: Route[] = [
    {
        href: 'convertrastertovector',
        title: 'Convert Raster To Vector',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <ConvertRasterToVector />
            </Suspense>
        ),
    },
    {
        href: 'improveartworkcontrast',
        title: 'Improve Artwork Contrast',
        element: <ImproveArtworkContrastPage />,
    },
    {
        href: 'cimdocenrichment',
        title: 'CimDoc Enrichment',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <CimDocEnrichmentPage />
            </Suspense>
        ),
    },
];

const artworkInspirationRoutes: Route[] = [
    {
        href: 'inspirationgenerate',
        title: 'Generate',
        element: <InspirationGeneratePage />,
    },
    {
        href: 'generate-image',
        title: 'Generate Image',
        element: <GenerateImage />,
    },
    {
        href: 'design-agent',
        title: 'Design Agent',
        externalHref: config.designAgentPlaygroundUrl,
        element: <DesignAgentMovedPage />,
    },
    {
        href: 'generate-logo',
        title: 'Generate Logo V3',
        element: <GenerateLogoPage />,
    },
    {
        href: 'generate-logo-v4',
        title: 'Generate Logo V4',
        element: <GenerateLogoV4Page />,
    },
    {
        href: 'generate-logo-prompt',
        title: 'Generate Logo Prompt',
        element: <GenerateLogoPromptPage />,
    },
    {
        href: 'edit-image',
        title: 'Edit Image',
        element: <EditImagePage />,
    },
    {
        href: 'stylize-photo',
        title: 'Stylize Photo',
        element: <StylizePhotoPage />,
    },

    {
        href: 'generate-pattern',
        title: 'Generate Pattern',
        element: <GeneratePatternPage />,
    },
];

const desigJobsRoutes: Route[] = [
    {
        href: 'design-job',
        title: 'Design Jobs',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DesignJobPage />
            </Suspense>
        ),
    },
];

const diamondParityRoutes: Route[] = [
    {
        href: 'diamond-parity',
        title: 'Overview',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DiamondParityPage />
            </Suspense>
        ),
    },
    {
        href: 'diamond-parity-static',
        title: 'Static',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DiamondParityStaticPage />
            </Suspense>
        ),
    },
    {
        href: 'diamond-parity-dynamic',
        title: 'Dynamic',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DiamondParityDynamicPage />
            </Suspense>
        ),
    },
    {
        href: 'diamond-parity-autolayout',
        title: 'Auto Layout',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DiamondParityAutoLayoutPage />
            </Suspense>
        ),
    },
    {
        href: 'diamond-parity-ppag',
        title: 'PPAG Usecase',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <PpagGalleryPage />
            </Suspense>
        ),
    },
    {
        href: 'diamond-parity-url-converter',
        title: 'URL Converter',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DiamondUrlConverterPage />
            </Suspense>
        ),
    },
];

export const hiddenRoutes: Route[] = [
    {
        href: 'dynamic-line-normalisation-report',
        title: 'Dynamic Line Normalisation Report',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DynamicLineNormalisationReport />
            </Suspense>
        ),
    },
    {
        href: 'hiddenpages',
        title: 'Hidden pages',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <HiddenPageIndexPage />
            </Suspense>
        ),
    },
    {
        href: 'design-agent-playground',
        title: 'Design Agent Playground',
        externalHref: config.designAgentPlaygroundUrl,
        element: <DesignAgentMovedPage />,
    },
    {
        href: 'diamond-parity',
        title: 'Diamond Parity',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <DiamondParityPage />
            </Suspense>
        ),
    },
    {
        href: 'composition-generate-stylized-assets',
        title: 'Composition Generate Stylized Assets',
        element: <ComposeStylizedAssetsPage />,
    },
    {
        href: 'settings',
        title: 'Settings',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <SettingsPage />
            </Suspense>
        ),
    },
    {
        href: 'personalized-gallery',
        title: 'Personalized Gallery',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <PersonalizedGallery />
            </Suspense>
        ),
    },
    {
        href: 'adapt-to-product',
        title: 'Adapt to Product',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <AdaptToProductPage />
            </Suspense>
        ),
    },
    {
        href: 'edit-image-v0',
        title: 'Edit Image V0',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <EditImagePageV0 />
            </Suspense>
        ),
    },
    {
        href: 'previewCards',
        title: 'PreviewCard Visualizations',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <PreviewCardVisualizationsPage />
            </Suspense>
        ),
    },
    {
        href: 'composition-auto-layout',
        title: 'Composition: Auto-layout',
        element: (
            <Suspense fallback={<PageLoadFallback />}>
                <CompositionAutoLayoutPage />
            </Suspense>
        ),
    },
    {
        href: 'legacy-to-design-job-comparison',
        title: 'Legacy to Design Job Comparison',
        element: <LegacyToDesignJobComparison />,
    },
    {
        href: '*',
        title: '404 - Not Found',
        element: (
            <FourOhFour message="Oops! The page you are looking for doesn't exist. Please check the URL or go back to the homepage." />
        ),
    },
];

export const routesByProblemSpace: ProblemSpaceRoutes[] = [
    {
        name: 'Artwork Adaptation',
        routes: artworkAdaptationRoutes,
    },
    {
        name: 'Artwork Composition',
        routes: artworkCompositionRoutes,
    },
    {
        name: 'Artwork Preservation',
        routes: artworkPreservationRoutes,
    },
    {
        name: 'Artwork Quality',
        routes: artworkQualityRoutes,
    },
    {
        name: 'Artwork Inspiration',
        routes: artworkInspirationRoutes,
    },
    {
        name: 'Design Jobs',
        routes: desigJobsRoutes,
    },
    {
        name: 'Diamond Parity',
        routes: diamondParityRoutes,
    },
];

export const routes: RouteWithProblemSpace[] = routesByProblemSpace.flatMap(({ name, routes }) =>
    routes.map((route) => ({ ...route, problemSpace: name })),
);
